/**
 * Shell: header, role-aware tabs, routing.
 *
 * The tabs mirror the legacy back office, but the rules behind them are no
 * longer decorative — hiding a tab is a convenience, and every route it leads
 * to is enforced server-side as well.
 */
import { useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { Role } from "@soi/contracts";
import { api } from "./lib/api.js";
import { queryKeys, useMe } from "./lib/queries.js";
import { useRealtime } from "./lib/realtime.js";
import { LoginDialog } from "./components/LoginDialog.js";
import { PublicBooking } from "./pages/PublicBooking.js";
import { Reservations } from "./pages/Reservations.js";
import { Deliveries } from "./pages/Deliveries.js";
import { Dashboard } from "./pages/Dashboard.js";
import { Stock } from "./pages/Stock.js";
import { Intervenants } from "./pages/Intervenants.js";
import { Users } from "./pages/Users.js";

interface Tab {
  path: string;
  label: string;
  roles: Role[];
}

/** Same split as the legacy `ONGLETS_PAR_ROLE`. */
const TABS: Tab[] = [
  { path: "/", label: "Plage de réservation", roles: ["admin", "intervenant"] },
  { path: "/gestion/reservations", label: "Suivi des réservations", roles: ["admin"] },
  { path: "/gestion/livraisons", label: "Mes livraisons", roles: ["intervenant"] },
  { path: "/gestion/tableau-de-bord", label: "Tableau de bord", roles: ["admin", "intervenant"] },
  { path: "/gestion/intervenants", label: "Intervenants", roles: ["admin"] },
  { path: "/gestion/utilisateurs", label: "Utilisateurs", roles: ["admin"] },
  { path: "/gestion/stocks", label: "Gestion des stocks", roles: ["admin", "intervenant"] },
];

function Guarded({ roles, children }: { roles: Role[]; children: React.ReactNode }) {
  const { data: me, isLoading } = useMe();
  const location = useLocation();
  if (isLoading) return <p className="empty">Chargement…</p>;
  if (!me) return <Navigate to="/" replace state={{ from: location.pathname }} />;
  if (!roles.includes(me.role)) {
    return (
      <div className="empty">
        Cet espace ne vous est pas ouvert.
        <br />
        <span className="muted">Connecté comme {me.email}.</span>
      </div>
    );
  }
  return <>{children}</>;
}

export function App() {
  const { data: me } = useMe();
  const [loginOpen, setLoginOpen] = useState(false);
  const [crestMissing, setCrestMissing] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useRealtime();

  const signOut = async () => {
    await api.logout();
    // Clear everything: leftover back-office data must not survive the session.
    queryClient.clear();
    navigate("/");
  };

  const visibleTabs = me ? TABS.filter((t) => t.roles.includes(me.role)) : [];

  return (
    <>
      <div className="top">
        <div className="wrap">
          <div className="brand">
            {/*
              The crest carries no wording, so the name stays as text beside it
              rather than being replaced by the image. `alt` is empty for that
              reason: with the words already in the markup, an alternative text
              would only make a screen reader say them twice.

              A missing file falls back to the shield drawn in CSS — which is
              what the header showed all along, the legacy page having
              referenced a crest it never shipped. Note that nginx answers 200
              with index.html for unknown paths, so the failure surfaces as an
              undecodable image rather than a 404.
            */}
            <img
              className="crest"
              src="/armoiries-ville-de-lausanne.png"
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = "none";
                setCrestMissing(true);
              }}
            />
            <span className="fallback" style={{ display: "flex" }}>
              {crestMissing && <i />}
              Ville de Lausanne
            </span>
            <b>Service d'organisation et d'informatique</b>
          </div>
          <button
            type="button"
            className={`admin-login-link${me ? " on" : ""}`}
            data-testid="admin-toggle"
            onClick={() => (me ? void signOut() : setLoginOpen(true))}
          >
            {me ? "Se déconnecter" : "Accès gestion"}
          </button>
        </div>
      </div>

      {me && (
        // The stylesheet keys off `#admin-tabs .tab[aria-selected=true]`, so the
        // class and the state must sit on the same element.
        <div id="admin-tabs" className="tabs" role="tablist" aria-label="Sections du back-office">
          <div className="wrap">
            {visibleTabs.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                className="tab"
                role="tab"
                aria-selected={pathname === tab.path}
                tabIndex={pathname === tab.path ? 0 : -1}
              >
                {tab.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      <Routes>
        <Route path="/" element={<PublicBooking />} />
        <Route
          path="/gestion/reservations"
          element={
            <Guarded roles={["admin"]}>
              <Reservations />
            </Guarded>
          }
        />
        <Route
          path="/gestion/livraisons"
          element={
            <Guarded roles={["intervenant"]}>
              <Deliveries />
            </Guarded>
          }
        />
        <Route
          path="/gestion/tableau-de-bord"
          element={
            <Guarded roles={["admin", "intervenant"]}>
              <Dashboard />
            </Guarded>
          }
        />
        <Route
          path="/gestion/stocks"
          element={
            <Guarded roles={["admin", "intervenant"]}>
              <Stock />
            </Guarded>
          }
        />
        <Route
          path="/gestion/intervenants"
          element={
            <Guarded roles={["admin"]}>
              <Intervenants />
            </Guarded>
          }
        />
        <Route
          path="/gestion/utilisateurs"
          element={
            <Guarded roles={["admin"]}>
              <Users />
            </Guarded>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {loginOpen && (
        <LoginDialog
          onClose={() => setLoginOpen(false)}
          onSuccess={(user) => {
            setLoginOpen(false);
            void queryClient.invalidateQueries({ queryKey: queryKeys.me });
            navigate(user.role === "admin" ? "/gestion/tableau-de-bord" : "/gestion/livraisons");
          }}
        />
      )}
    </>
  );
}
